import React from 'react';
import { useGesture } from '@use-gesture/react';

// Doc: https://use-gesture.netlify.app/docs/

/**
 * Options for pinch operations in {@link GestureHandlerProps}.
 */
export type PinchOptions = {
  /**
   * Limits scaling to the specified bounds.
   */
  pinchBounds: { min: number; max: number };
};

/**
 * Options for drag operations in {@link GestureHandlerProps}.
 */
export type DragOptions = {
  /**
   * The initial position of the drag operation.
   */
  start: { x: number; y: number };
  /**
   * The handler will fire only when the gesture displacement is greater than the threshold.
   */
  threshold?: number;
  /**
   * If set, the handler will be delayed for the duration of the delay (in ms) — or if the user starts moving.
   */
  delay?: number;
};

/**
 * Options for swipe operations in {@link GestureHandlerProps}.
 */
export type SwipeOptions = {
  /**
   * The minimum velocity per axis (in pixels / ms) the drag gesture needs to reach before the pointer is released.
   */
  minVelocity: number;
};

/**
 * Props for a {@link GestureHandler} component.
 */
export type GestureHandlerProps = {
  onDrag?: (x: number, y: number) => void;
  onPinch?: (
    xStart: number,
    yStart: number,
    scale: number,
    rotateZ: number
  ) => void;
  onSwipe?: (swipeX: number, swipeY: number) => void;
  onWheel?: (deltaY: number) => void;
  onClick?: () => void;
  onDoubleClick?: () => void;
  onPointerDown?: () => void;
  onPointerUp?: () => void;
  pinchOptions?: PinchOptions;
  dragOptions?: DragOptions;
  swipeOptions?: SwipeOptions;
  children?: React.ReactNode;
};

/**
 * A component to handle gestures that can be used for mobile and desktop browsers
 */
export function GestureHandler({
  onClick,
  onDoubleClick,
  onDrag,
  onPointerDown,
  onPointerUp,
  onPinch,
  onSwipe,
  onWheel,
  dragOptions = {
    start: { x: 0, y: 0 }
  },
  pinchOptions = {
    pinchBounds: { min: 0.5, max: 2 }
  },
  swipeOptions = {
    minVelocity: 0.3
  },
  children,
  ...props
}: Readonly<GestureHandlerProps>): JSX.Element {
  const controlRef = React.createRef<HTMLDivElement>();
  const getSwipeDirection = (distance: number) => {
    if (distance > 0) {
      return 1;
    } else if (distance < 0) {
      return -1;
    }
    return 0;
  };

  useGesture(
    {
      onDrag: ({
        pinching,
        dragging,
        offset: [x, y],
        velocity: [vx, vy],
        movement: [mx, my],
        last
      }) => {
        if (pinching) {
          return;
        }
        if (dragging) {
          onDrag?.(x, y);
        } else if (
          last &&
          (vx >= swipeOptions.minVelocity || vy >= swipeOptions.minVelocity)
        ) {
          onSwipe?.(getSwipeDirection(mx), getSwipeDirection(my));
        }
      },
      onPinch: ({
        pinching,
        origin: [xStart, yStart],
        offset: [scale, rotateZ]
      }) => {
        if (onPinch && pinching) {
          onPinch(xStart, yStart, scale, rotateZ);
        }
      },
      onWheel: ({ delta: [deltaX, deltaY] }) => {
        onWheel?.(deltaY);
      }
    },
    {
      target: controlRef,
      drag: {
        from: () => [dragOptions.start.x, dragOptions.start.y],
        threshold: dragOptions.threshold ?? 5,
        delay: dragOptions.delay ?? 700
      },
      pinch: { scaleBounds: pinchOptions.pinchBounds, rubberband: false }
    }
  );
  return (
    <div
      {...props}
      ref={controlRef}
      className={'gesture-handler'}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
    >
      {children}
    </div>
  );
}
